import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Wallet, TriangleAlert, Loader2, TrendingDown, Ban, Plus,
  Warehouse, EyeOff, MapPin, Pencil,
} from 'lucide-react';
import * as api from '../../lib/api';
import { useAuthStore } from '../../store/useAuthStore';
import { formatCents, formatFeet } from '../../lib/format';
import { SPACE_TYPE_LABELS } from '../../lib/fit';
import { uploadUrl } from '../../lib/uploads';
import Photo from '../../components/ui/Photo';
import { StatusPill } from '../../components/admin/unitStatus';
import EditUnitDialog from '../../components/admin/EditUnitDialog';

/**
 * Operations: the yards and what is in them.
 *
 * Deliberately holds no customer roster. Who is renting is a question the
 * tenant directory answers with search and paging behind it; this screen
 * answers "what do we have and what state is it in", and a hundred rows of
 * names between the operator and their inventory only made that harder.
 */
export default function AdminHome() {
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.adminOverview()
      .then((payload) => { if (!cancelled) { setData(payload); setState('ready'); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setState('error'); } });
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }
  if (state === 'error') {
    return <p className="mx-auto mt-8 max-w-md rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>;
  }

  const { totals, facilities, scope } = data;
  const isOwner = user?.role === 'super_admin';

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Operations</h1>
      <p className="mt-1 text-sm text-slate-600">
        {user?.first_name} · {scope === 'all_facilities'
          ? `all ${totals.facility_count} facilities`
          : `${totals.facility_count} assigned facilit${totals.facility_count === 1 ? 'y' : 'ies'}`}
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile Icon={Building2} label="Occupancy" value={`${totals.occupancy_pct}%`}
              hint={`${totals.occupied} of ${totals.total} spaces`} />
        <Tile Icon={Warehouse} label="Available now" value={totals.available}
              hint={`${totals.reserved} reserved · ${totals.maintenance} out of service`} />
        <Tile Icon={Wallet} label="Monthly revenue" value={formatCents(totals.occupied_rent_cents)}
              hint={`${formatCents(totals.potential_rent_cents)} at full occupancy`} />
        <Tile Icon={TriangleAlert} label="Outstanding" value={formatCents(totals.outstanding_cents)}
              hint={`${totals.past_due_invoices} invoice${totals.past_due_invoices === 1 ? '' : 's'} past due`}
              warn={totals.outstanding_cents > 0} />
      </div>

      {/* The number an operator actually manages against: empty space costs money. */}
      <div className="mt-4 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white p-4">
        <TrendingDown className="h-5 w-5 shrink-0 text-brand-500" aria-hidden="true" />
        <p className="text-sm text-slate-700">
          <strong className="font-extrabold text-slate-900">{formatCents(totals.vacancy_cost_cents)}/month</strong>
          {' '}of empty space — {totals.available} available, {totals.reserved} reserved, {totals.maintenance} out of service.
        </p>
        {totals.blocking_tickets > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-2.5 py-1 text-[12px] font-bold text-rose-800">
            <Ban className="h-3.5 w-3.5" aria-hidden="true" />
            {totals.blocking_tickets} unit{totals.blocking_tickets === 1 ? '' : 's'} unrentable pending repair
          </span>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-900">Facilities and their spaces</h2>
        {isOwner && (
          <Link to="/admin/facilities/new"
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-bold text-white hover:bg-brand-700">
            <Plus className="h-4 w-4" aria-hidden="true" /> New facility
          </Link>
        )}
      </div>

      {facilities.length === 0 ? (
        <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center">
          <Building2 className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">No facilities yet</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
            {isOwner
              ? 'Add the first one to start tracking occupancy and revenue.'
              : 'You are not assigned to a facility yet. Ask an owner to add you to one.'}
          </p>
          {isOwner && (
            <Link to="/admin/facilities/new" className="mt-3 inline-block text-xs font-bold text-brand-700 hover:underline">
              Add a facility
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-6">
          {facilities.map((f) => <FacilityPanel key={f.public_id} facility={f} />)}
        </div>
      )}
    </div>
  );
}

/**
 * One yard: its photo, its numbers, and every space in it.
 *
 * Units are fetched per facility rather than folded into the overview payload,
 * because they are the slow part and the numbers above are what the screen is
 * opened for. A facility whose units fail to load still shows its figures and
 * says so on its own row, instead of taking the page down with it.
 */
function FacilityPanel({ facility: f }) {
  const [units, setUnits] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.adminUnits(f.public_id)
      .then((payload) => { if (!cancelled) setUnits(payload.units); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [f.public_id, reloadToken]);

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-4 border-b border-slate-100 p-4 sm:flex-row">
        <Photo src={uploadUrl(f.photos?.[0])} alt={f.name}
               className="h-32 w-full shrink-0 rounded-lg object-cover sm:h-24 sm:w-40" loading="lazy" />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-extrabold text-slate-900">{f.name}</h3>
            {f.is_published === false && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[12px] font-bold text-slate-600">
                <EyeOff className="h-3 w-3 text-brand-500" aria-hidden="true" /> Unlisted
              </span>
            )}
          </div>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
            <MapPin className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
            {f.city}, {f.state_code}
          </p>

          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
            <Metric label="Occupancy" value={`${f.occupancy_pct}%`} sub={`${f.occupied_units}/${f.total_units}`} />
            <Metric label="Available" value={f.available_units} />
            <Metric label="Revenue" value={formatCents(f.monthly_revenue_cents)} />
            <Metric label="Outstanding" value={formatCents(f.outstanding_cents)}
                    tone={f.outstanding_cents > 0 ? 'bad' : undefined} />
            <Metric label="Open tickets" value={f.open_tickets}
                    tone={f.open_tickets > 0 ? 'warn' : undefined} />
          </dl>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col">
          <Link to={`/admin/facilities/${f.public_id}/units`}
                className="rounded-lg bg-brand-600 px-3 py-2 text-xs font-bold text-white hover:bg-brand-700">
            Manage spaces
          </Link>
          <Link to={`/admin/facilities/${f.public_id}/edit`}
                className="rounded-lg border border-slate-300 px-3 py-2 text-center text-xs font-bold text-slate-700 hover:bg-slate-50">
            Edit facility
          </Link>
        </div>
      </div>

      {saveError && (
        <p className="border-b border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-800">{saveError}</p>
      )}

      <UnitTable units={units} failed={failed} facilityId={f.public_id} onEdit={setEditing} />

      {editing && (
        <EditUnitDialog
          unit={editing}
          facilityId={f.public_id}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setSaveError(null);
            // Re-fetch rather than patching the row in place: a status change
            // moves the facility's occupancy and available counts too, and a
            // table that disagrees with the figures above it is worse than a
            // brief spinner.
            setReloadToken((n) => n + 1);
          }}
          onError={setSaveError}
        />
      )}
    </section>
  );
}

function UnitTable({ units, failed, facilityId, onEdit }) {
  if (failed) {
    return (
      <p className="px-4 py-6 text-sm text-amber-800">
        Could not load the spaces for this facility. The figures above are still accurate.{' '}
        <Link to={`/admin/facilities/${facilityId}/units`} className="font-bold underline">Open them directly</Link>.
      </p>
    );
  }

  if (units === null) {
    return (
      <div className="grid place-items-center py-10" role="status" aria-label="Loading spaces">
        <Loader2 className="h-5 w-5 animate-spin text-brand-600" aria-hidden="true" />
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <div className="px-4 py-10 text-center">
        <Warehouse className="mx-auto h-7 w-7 text-brand-300" aria-hidden="true" />
        <p className="mt-2 text-sm font-bold text-slate-900">No spaces here yet</p>
        <p className="mt-0.5 text-sm text-slate-500">
          A facility with no spaces cannot be rented or found in search.{' '}
          <Link to={`/admin/facilities/${facilityId}/units`} className="font-bold text-brand-700 hover:underline">
            Add some
          </Link>.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[38rem] text-sm">
        <caption className="sr-only">Spaces at this facility</caption>
        <thead className="border-b border-slate-200 bg-slate-50 text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2.5">Space</th>
            <th className="px-4 py-2.5">Type</th>
            <th className="px-4 py-2.5">Size</th>
            <th className="px-4 py-2.5">Access</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5 text-right">Rate</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {units.map((u) => (
            <tr key={u.public_id} className="hover:bg-slate-50">
              <td className="px-4 py-2.5 font-bold text-slate-900">{u.unit_number}</td>
              <td className="px-4 py-2.5 text-slate-600">{SPACE_TYPE_LABELS[u.space_type] ?? u.space_type}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                {formatFeet(u.length_ft)} × {formatFeet(u.width_ft)}
                {u.height_clearance_ft != null && (
                  <span className="text-slate-400"> · {formatFeet(u.height_clearance_ft)} clear</span>
                )}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                {u.is_pull_through ? 'Pull-through' : 'Back-in'}
              </td>
              <td className="px-4 py-2.5"><StatusPill status={u.status} /></td>
              <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-slate-900">
                {formatCents(u.monthly_rate_cents)}
              </td>
              <td className="px-4 py-2.5 text-right">
                <button type="button" onClick={() => onEdit(u)}
                        aria-label={`Edit space ${u.unit_number}`}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs font-bold text-brand-700 hover:bg-slate-50">
                  <Pencil className="h-3.5 w-3.5 text-brand-600" aria-hidden="true" /> Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Metric({ label, value, sub, tone }) {
  const valueTone = tone === 'bad' ? 'text-rose-700' : tone === 'warn' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div>
      <dt className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`text-sm font-extrabold tabular-nums ${valueTone}`}>
        {value}
        {sub && <span className="ml-1 text-xs font-medium text-slate-400">{sub}</span>}
      </dd>
    </div>
  );
}

function Tile({ Icon, label, value, hint, warn }) {
  return (
    <div className={`rounded-xl border p-4 ${warn ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <Icon className={`h-5 w-5 ${warn ? 'text-amber-600' : 'text-brand-600'}`} aria-hidden="true" />
      <p className="mt-3 text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-[12px] text-slate-400">{hint}</p>
    </div>
  );
}
